import React from "react";
import { motion } from "framer-motion";
import { FaLinkedin} from "react-icons/fa";

export default function Home() {
  return (
    <div className="bg-[#C4C5BA] text-black min-h-screen font-light">
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 w-full bg-black backdrop-blur-lg shadow-md py-6 px-10 flex justify-between items-center z-50">
        <h1 className="text-2xl font-semibold tracking-wide ">Quintellics</h1>
        <div className="space-x-8 text-lg">
          <motion.a 
            href="#services" 
            className="text-[#C4C5BA] transition relative inline-block"
            whileHover={{ scale: 1.2, x: 5 }}
          >
            Services
          </motion.a>
          <motion.a 
            href="#contact" 
            className="text-[#C4C5BA] transition relative inline-block"
            whileHover={{ scale: 1.2, x: 5 }}
          >
            Contact
          </motion.a>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="h-screen flex flex-col justify-center items-center text-center px-6 relative overflow-hidden">
        <motion.h1
          className="text-6xl md:text-5xl font-semibold relative z-10 drop-shadow-lg max-w-4xl leading-tight"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          Intelligent Insights. Smarter Decisions.
        </motion.h1>
        <motion.p
          className="mt-6 text-xl text-gray-700 font-light relative z-10 max-w-3xl"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          Transform your business with AI-driven analytics and data-powered intelligence.
        </motion.p>
      </section>

      {/* Services Section */}
      <section id="services" className="py-32 px-10 relative text-center">
        <h2 className="text-4xl md:text-5xl font-semibold relative z-10">Our Services</h2>
        <p className="text-gray-600 mt-6 max-w-3xl mx-auto relative z-10 text-lg">
          Unlock the full potential of your data with our intelligent, customized solutions designed to enhance decision-making, optimize operations, and drive business growth.
        </p>
        <div id="card" className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-16 relative z-10">
          <ServiceCard title="Data Analytics" desc="Transform vast amounts of raw data into actionable intelligence, uncovering deep insights that drive strategic growth, enhance operational efficiency, and create a competitive edge in your industry. Our advanced analytics solutions help identify patterns, predict trends, and optimize decision-making, ensuring your business remains agile, adaptive, and data-driven in an ever-evolving and competitive landscape, allowing for continuous improvement and innovation." className="md:col-span-2 row-span-1" />
          <ServiceCard title="Business Intelligence" desc="Enhance decision-making with advance BI solutions that offer real-time insights, streamline operations, and effectively optimize business processes for better evolved overall performance."/>
          <ServiceCard title="KPI Dashboards" desc="Monitor performance with real-time dashboards and optimize business strategies using key metric insights for improved efficiency, overall productivity, and significant business success."/>
          <ServiceCard title="Data Visualization" desc="Simplify complex datasets with visually compelling graphics for better insights. Our AI visualization tools transform raw numbers into meaningful stories while making data interpretation impactful." />
          <ServiceCard title="Process Automation" desc="Eliminate inefficiencies and enhance productivity through intelligent workflow automation. Our AI-driven automation solutions streamline repetitive tasks and reduce errors for high-impact deliverables." />
          <ServiceCard title="Data Strategy Consulting" desc="Comprehensive and future-ready analytics strategy to drive sustainable growth and foster innovation. Our strategic services assist businesses in defining their data roadmap, integrating cutting-edge technologies, and optimizing decision-making frameworks. By leveraging our expertise and industry knowledge, companies can solve complexities of the digital landscape, enhance data accessibility, and empower teams with actionable insights for success and competitive advantage." className="md:col-span-2" />
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 px-10 text-center relative">
        <h2 className="text-4xl md:text-5xl font-semibold relative z-10">Get in Touch</h2>
        <p className="mt-6 text-xl text-gray-700 font-light max-w-2xl mx-auto relative z-10">
          Ready to elevate your business? Let’s discuss how we can unlock the power of data for you.
        </p>
        <a id="mail" href="https://docs.google.com/forms/d/e/1FAIpQLScMdBOwWIKfE5hvZbHFfnqiKqwk2Eh3wBswCUqZHyD-nhb-Ng/viewform?usp=dialog" className="mt-8 inline-block bg-black px-8 py-4 rounded-full font-semibold transition relative z-10 text-lg transform hover:scale-105">Contact Us</a>
      </section>

      {/* Footer Section */}
      <footer className="bg-black text-[#C4C5BA] py-6 px-10 flex justify-between items-center mt-10">
        <p className="text-lg">© 2025 Quintellics. All rights reserved.</p>
        <div className="flex space-x-6">
          <a href="https://www.linkedin.com/company/quintellics-llp/" className="text-[#C4C5BA] text-2xl hover:scale-110 transition"><FaLinkedin /></a>
        </div>
      </footer>
    </div>
  );
}

const ServiceCard = ({ title, desc, className }) => (
  <motion.div
    className={`bg-black p-8 rounded-xl text-left shadow-lg hover:shadow-2xl transition transform hover:scale-105 font-light relative z-10 border border-gray-300 flex flex-col justify-between text-[#C4C5BA] ${className}`}
    whileHover={{ scale: 1.07 }}
  >
    <h3 className="text-2xl font-semibold text-[#C4C5BA] mb-4">{title}</h3>
    <p className="text-[#C4C5BA] text-lg leading-relaxed">{desc}</p>
  </motion.div>
);
